import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { LangProvider } from "@/lib/i18n";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SiteConfigProvider } from "@/context/SiteConfigContext";
import { ContentProvider } from "@/context/ContentContext";
import { captureUTM } from "@/lib/api";

import PublicLayout from "@/components/layout/PublicLayout";
import Home from "@/pages/public/Home";
import FamilyOffice from "@/pages/public/FamilyOffice";
import RealEstate from "@/pages/public/RealEstate";
import Maklerservice from "@/pages/public/Maklerservice";
import Development from "@/pages/public/Development";
import Festgeld from "@/pages/public/Festgeld";
import Leistungen from "@/pages/public/Leistungen";
import UeberUns from "@/pages/public/UeberUns";
import Privatkunden from "@/pages/public/Privatkunden";
import BeraterFinden from "@/pages/public/BeraterFinden";
import Karriere from "@/pages/public/Karriere";
import Termin from "@/pages/public/Termin";
import Anfrage from "@/pages/public/Anfrage";
import Legal from "@/pages/public/Legal";
import Login from "@/pages/Login";
import EnforceTwoFactor from "@/pages/EnforceTwoFactor";
import ForcedPasswordChange from "@/pages/ForcedPasswordChange";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";

import AppShell from "@/components/portal/AppShell";
import Dashboard from "@/pages/portal/Dashboard";
import Leads from "@/pages/portal/Leads";
import Tasks from "@/pages/portal/Tasks";
import Documents from "@/pages/portal/Documents";
import GeneratedDoc from "@/pages/portal/GeneratedDoc";
import Compliance from "@/pages/portal/Compliance";
import Profile from "@/pages/portal/Profile";
import Academy from "@/pages/academy/Academy";
import CoursePlayer from "@/pages/academy/CoursePlayer";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminCourses from "@/pages/admin/AdminCourses";
import CourseBuilder from "@/pages/admin/CourseBuilder";
import AdminDocuments from "@/pages/admin/AdminDocuments";
import TemplateEditor from "@/pages/admin/TemplateEditor";
import AdminContent from "@/pages/admin/AdminContent";
import AdminForms from "@/pages/admin/AdminForms";
import AdminEmails from "@/pages/admin/AdminEmails";
import AdminCRM from "@/pages/admin/AdminCRM";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminSettings from "@/pages/admin/AdminSettings";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" data-testid="auth-loading" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  useEffect(() => {
    captureUTM();
  }, []);

  return (
    <LangProvider>
      <SiteConfigProvider>
        <ContentProvider>
        <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/family-office" element={<FamilyOffice />} />
              <Route path="/real-estate" element={<RealEstate />} />
              <Route path="/maklerservice" element={<Maklerservice />} />
              <Route path="/development" element={<Development />} />
              <Route path="/leistungen" element={<Leistungen />} />
              <Route path="/leistungen/nettopolicen" element={<Navigate to="/maklerservice" replace />} />
              <Route path="/leistungen/immobilien" element={<Navigate to="/real-estate" replace />} />
              <Route path="/leistungen/festgeld" element={<Festgeld />} />
              <Route path="/privatkunden" element={<Privatkunden />} />
              <Route path="/ueber-uns" element={<UeberUns />} />
              <Route path="/berater-finden" element={<BeraterFinden />} />
              <Route path="/karriere" element={<Karriere />} />
              <Route path="/termin" element={<Termin />} />
              <Route path="/anfrage/:type" element={<Anfrage />} />
              <Route path="/impressum" element={<Legal page="impressum" />} />
              <Route path="/datenschutz" element={<Legal page="datenschutz" />} />
              <Route path="/mandanten" element={<Navigate to="/privatkunden" replace />} />
            </Route>
            <Route path="/login" element={<Login />} />
            <Route path="/2fa-einrichten" element={<EnforceTwoFactor />} />
            <Route path="/passwort-aendern" element={<ForcedPasswordChange />} />
            <Route path="/passwort-vergessen" element={<ForgotPassword />} />
            <Route path="/passwort-zuruecksetzen" element={<ResetPassword />} />
            <Route
              element={
                <RequireAuth>
                  <AppShell />
                </RequireAuth>
              }
            >
              <Route path="/portal" element={<Dashboard />} />
              <Route path="/portal/leads" element={<Leads />} />
              <Route path="/portal/aufgaben" element={<Tasks />} />
              <Route path="/portal/dokumente" element={<Documents />} />
              <Route path="/portal/dokumente/g/:genId" element={<GeneratedDoc />} />
              <Route path="/portal/compliance" element={<Compliance />} />
              <Route path="/portal/profil" element={<Profile />} />
              <Route path="/academy" element={<Academy />} />
              <Route path="/academy/kurs/:id" element={<CoursePlayer />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/benutzer" element={<AdminUsers />} />
              <Route path="/admin/kurse" element={<AdminCourses />} />
              <Route path="/admin/kurse/:courseId" element={<CourseBuilder />} />
              <Route path="/admin/dokumente" element={<AdminDocuments />} />
              <Route path="/admin/dokumente/vorlagen/:templateId" element={<TemplateEditor />} />
              <Route path="/admin/inhalte" element={<AdminContent />} />
              <Route path="/admin/formulare" element={<AdminForms />} />
              <Route path="/admin/postausgang" element={<AdminEmails />} />
              <Route path="/admin/crm" element={<AdminCRM />} />
              <Route path="/admin/audit" element={<AdminAudit />} />
              <Route path="/admin/einstellungen" element={<AdminSettings />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
        </ContentProvider>
      </SiteConfigProvider>
    </LangProvider>
  );
}

export default App;
